import React, { useEffect, useState } from 'react'
import { Accordion, Button, Modal } from 'react-bootstrap'
import LabSharedTable from '@components/pages/Lab/LabSharedTable'
import { getFileIcon } from '@utils/file.utils'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import PrescriptionNote from './PrescriptionNote'

type Attachment = {
  file: string | File
  date: string
}

type PrescriptionDetail = {
  procedureType: string
  region: string
  material: string
  shadeNo: string
  instructions: string
}

type ReviewOrderProps = {
  data: {
    patientName: string
    labWorkType: string
    providerName: string
    partnerLabName: string
    dueDate: string
    priority: string
    prescriptionDetails?: PrescriptionDetail[]
    attachments?: Attachment[]
  }
  onBack: () => void
}

const ReviewOrder: React.FC<ReviewOrderProps> = ({ data, onBack }) => {
  const {
    patientName,
    labWorkType,
    providerName,
    partnerLabName,
    dueDate,
    priority,
    prescriptionDetails: initialPrescriptionDetails = [],
    attachments: initialAttachments = [],
  } = data

  const [prescriptionDetails, setPrescriptionDetails] = useState<
    PrescriptionDetail[]
  >([])
  const [showNote, setShowNote] = useState<boolean>(false)
  const [currentNote, setCurrentNote] = useState<string>('')
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null)
  const [isReadonly, setIsReadonly] = useState<boolean>(true)
  const [showAttachmentModal, setShowAttachmentModal] = useState<boolean>(false)
  const [selectedAttachment, setSelectedAttachment] =
    useState<Attachment | null>(null)

  useEffect(() => {
    setPrescriptionDetails(initialPrescriptionDetails)
  }, [initialPrescriptionDetails])

  const openNoteModal = (row: PrescriptionDetail): void => {
    const rowIndex = prescriptionDetails.findIndex(
      (r) => r.procedureType === row.procedureType && r.region === row.region,
    )
    setCurrentNote(row.instructions)
    setSelectedRowIndex(rowIndex)
    setIsReadonly(!!row.instructions)
    setShowNote(true)
  }

  const handleChangeNote = (index: number, newNote: string): void => {
    const updated = [...prescriptionDetails]
    updated[index].instructions = newNote
    setPrescriptionDetails(updated)
  }

  const handleSaveNote = (): void => {
    if (selectedRowIndex !== null) {
      const updated = [...prescriptionDetails]
      updated[selectedRowIndex].instructions = currentNote
      setPrescriptionDetails(updated)
    }
    setShowNote(false)
  }

  const truncate = (str: string, max = 15): string =>
    str.length > max ? str.slice(0, max) + '…' : str

  const prescriptionColumns = [
    { key: 'procedureType', label: 'Procedure', style: { width: '120px' } },
    { key: 'region', label: 'Region', style: { width: '160px' } },
    { key: 'material', label: 'Material', style: { width: '120px' } },
    { key: 'shadeNo', label: 'Shade', style: { width: '100px' } },
    {
      key: 'instructions',
      label: 'Instructions',
      style: { width: '200px' },
      formatter: (_: any, row: PrescriptionDetail) => (
        <Button
          variant="link"
          size="sm"
          className="p-0 text-decoration-underline"
          onClick={() => openNoteModal(row)}
        >
          {row.instructions ? 'View' : 'Add note'}
        </Button>
      ),
    },
  ]

  const processedAttachments = initialAttachments.map((file, index) => ({
    ...file,
    itemNo: index + 1,
  }))

  const attachmentColumns = [
    { key: 'itemNo', label: 'Item #', style: { width: '100px' } },
    {
      key: 'itemIcon',
      label: 'Item',
      style: { width: '80px' },
      formatter: (_: any, row: Attachment) => {
        const fileName =
          typeof row.file === 'string' ? row.file : (row.file?.name ?? '')
        return (
          <div className="file-icon-wrapper" title={fileName}>
            <i className={`${getFileIcon(fileName)} file-icon fs-2`} />
          </div>
        )
      },
    },
    {
      key: 'formatType',
      label: 'Format type',
      style: { width: '180px' },
      formatter: (_: any, row: Attachment) => {
        const fileName =
          typeof row.file === 'string' ? row.file : (row.file?.name ?? '')
        return <span>{fileName.split('.').pop()?.toUpperCase() || 'N/A'}</span>
      },
    },
    {
      key: 'file',
      label: 'File name',
      style: { width: '100px' },
      formatter: (val: any) => {
        const fileName = typeof val === 'string' ? val : val?.name || ''
        return truncate(fileName, 15)
      },
    },
    { key: 'date', label: 'Date taken', style: { width: '150px' } },
    {
      key: 'actions',
      label: 'Actions',
      style: { width: '150px', textAlign: 'center' as const },
      formatter: (_: any, row: Attachment) => (
        <ActionButtons
          onView={() => {
            setSelectedAttachment(row)
            setShowAttachmentModal(true)
          }}
        />
      ),
    },
  ]

  return (
    <div className="p-0 review-order-section">
      <div className="d-flex justify-content-between align-items-center pb-1">
        <h4 className="order-review-title">Review the information below</h4>
        <Button
          size="sm"
          variant="tertiary"
          className="text-blue"
          onClick={onBack}
        >
          <i className="ti ti-pencil pe-1 fs-6"></i> Edit
        </Button>
      </div>

      <Accordion defaultActiveKey="0" flush>
        <Accordion.Item eventKey="0">
          <Accordion.Header>Scheduling</Accordion.Header>
          <Accordion.Body className="p-2">
            {[
              ['Patient', patientName],
              ['Lab work type', labWorkType],
              ['Provider', providerName],
              ['Partner lab', partnerLabName],
              ['Due date', dueDate],
              ['Priority', priority],
            ].map(([label, value]) => (
              <div className="row py-1" key={label}>
                <div className="col col-review-label">{label}</div>
                <div className="col col-review-value text-end">{value}</div>
              </div>
            ))}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>Prescription details</Accordion.Header>
          <Accordion.Body className="p-0 prescription-accordion-table">
            <LabSharedTable
              data={prescriptionDetails}
              columns={prescriptionColumns}
              pageSize={0}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="2">
          <Accordion.Header>Attachments</Accordion.Header>
          <Accordion.Body className="p-0 lab-attachment-accordion-table">
            <LabSharedTable
              data={processedAttachments}
              columns={attachmentColumns}
              pageSize={0}
            />
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <PrescriptionNote
        show={showNote}
        notes={prescriptionDetails.map((item) => ({
          procedureName: item.procedureType,
          instructions: item.instructions,
        }))}
        title={isReadonly ? 'Order instructions' : 'Add note'}
        readonly={isReadonly}
        onChange={handleChangeNote}
        onSave={handleSaveNote}
        onClose={() => setShowNote(false)}
      />

      <Modal
        show={showAttachmentModal}
        onHide={() => setShowAttachmentModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Attachment view</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAttachment?.file && (
            <>
              <div>
                <strong>File name:</strong>{' '}
                {typeof selectedAttachment.file === 'string'
                  ? selectedAttachment.file
                  : selectedAttachment.file.name}
              </div>
              <div className="mt-2">
                <strong>Date taken:</strong> {selectedAttachment.date}
              </div>
              <div className="text-center mt-2">
                {typeof selectedAttachment.file === 'object' &&
                selectedAttachment.file.type?.startsWith('image') ? (
                  <img
                    src={URL.createObjectURL(selectedAttachment.file)}
                    alt="preview"
                    style={{ maxWidth: '100%', maxHeight: '400px' }}
                  />
                ) : (
                  <p>No preview available</p>
                )}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default ReviewOrder
